import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NO_DRAG_SELECTOR } from "../../../../../../app/constants";
import DialogContentWrapper from "../../../../../../common/components/functional/dialogs/DialogContentWrapper";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import {
  getWorkTitleSpecificPricingDefaultValues,
  preparePricingCategoriesForApi,
} from "../../../helpers/pricingUtils";
import { StyledFormGroup } from "../../../styles";
import WorkAndAbsencesSection from "../workAndAbscences/WorkAndAbsencesSection";
import WorkTitleSection from "./WorkTitleSection";
import { UnsavedChangesProvider } from "../../../../../../contexts/UnsavedChangesContext";

type Props = {
  selectedItemId?: string;
  disabled?: boolean;
  onClose: () => void;
  canEdit: boolean;
};

const MAIN_FORM_PROPERTY_KEY = "worktitles";

// Set individual work title pricing details
const WorkTitleSpecificPricingForm = ({
  selectedItemId,
  disabled,
  onClose,
  canEdit,
}: Props) => {
  const { t } = useTranslation("customers");
  const { getValues: getMainFormValues, setValue: setMainFormValue } =
    useFormContext();

  const isEditMode = !!selectedItemId;

  const existingTesId = getMainFormValues("tesId");

  // Get existing values if they exist (edit mode)
  const mainFormValues = getMainFormValues(MAIN_FORM_PROPERTY_KEY);
  const existingWorkTitles = mainFormValues?.map((wt: any) => wt.workTitleId);
  const existingValues = mainFormValues?.find(
    (obj: any) => obj.workTitleId === selectedItemId,
  );
  const defaultValues = getWorkTitleSpecificPricingDefaultValues(
    existingValues || {},
  );

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const {
    watch,
    handleSubmit,
    formState: { isDirty, errors },
  } = methods;

  const onSubmit = (formData: any) => {
    const formObject = {
      workTitleId: formData.workTitleId,
      categories: preparePricingCategoriesForApi(formData.categories),
    };
    const updatedWorkTitlesArray = isEditMode
      ? mainFormValues.map((workTitleObj: any) => {
          if (workTitleObj.workTitleId === selectedItemId) return formObject;
          return workTitleObj;
        })
      : [...mainFormValues, formObject];
    setMainFormValue(MAIN_FORM_PROPERTY_KEY, updatedWorkTitlesArray, {
      shouldDirty: true,
    });
    onClose();
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => onClose();

  const watchedWorkTitleId = watch("workTitleId");

  const saveDisabled = !isDirty || !watchedWorkTitleId || !isEmpty(errors);

  return (
    <DialogContentWrapper
      title={t("contract.workTitleSpecificPricing")}
      margin
      onCancel={onCancel}
      onSave={onSave}
      saveDisabled={saveDisabled}
      containerSx={{
        height: "100%",
      }}
      childContainerSx={{ overflowY: "auto" }}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <StyledFormGroup className={NO_DRAG_SELECTOR}>
            <WorkTitleSection
              disabled={isEditMode || disabled}
              existingWorkTitles={existingWorkTitles}
            />
            <WorkAndAbsencesSection
              tesId={existingTesId}
              disabled={disabled}
              canEdit={canEdit}
            />
          </StyledFormGroup>
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default WorkTitleSpecificPricingForm;
